export type MotifAlerte = 'sante' | 'securite' | 'ecole' | 'passage_de_bras';

export interface Alerte {
  id: string;
  level: 'normal' | 'urgent';
  reason: MotifAlerte;
  body: string;
  createdAt: string;
  authorId: string | null;
  acknowledgedAt: string | null;
  downgraded: boolean;
  /** Sonnerie demandée par l'auteur. Le réglage du destinataire l'emporte. */
  sound: boolean;
}

/** Les deux défenses du destinataire, telles que l'API les rend. */
export interface ReglagesUrgence {
  urgentSound: boolean;
  urgentTakeover: boolean;
}

/**
 * Les quatre motifs, et rien d'autre.
 *
 * La liste est fermée côté serveur (`AlertReason`) : c'est le premier garde-fou du
 * dispositif, et l'écran ne fait que la refléter. Un champ libre à la place aurait rendu
 * l'alerte utilisable pour n'importe quoi — donc pour rien.
 */
export const MOTIFS: { valeur: MotifAlerte; libelle: string; exemple: string }[] = [
  { valeur: 'sante', libelle: 'Santé', exemple: 'Fièvre, blessure, passage aux urgences' },
  { valeur: 'securite', libelle: 'Sécurité', exemple: 'L’enfant est introuvable, accident' },
  { valeur: 'ecole', libelle: 'École', exemple: 'Appel de l’établissement, sortie annulée' },
  {
    valeur: 'passage_de_bras',
    libelle: 'Passage de bras',
    exemple: 'Retard, empêchement, lieu changé',
  },
];

export const LIBELLE_MOTIF: Record<MotifAlerte, string> = {
  sante: 'Santé',
  securite: 'Sécurité',
  ecole: 'École',
  passage_de_bras: 'Passage de bras',
};

/**
 * L'alerte qui doit s'imposer à l'écran, s'il y en a une.
 *
 * Trois conditions, et la troisième est la moins évidente : **la plus ancienne d'abord**.
 * Afficher la plus récente ferait disparaître une alerte non accusée derrière une seconde,
 * et la première — celle qui attend depuis le plus longtemps — ne serait jamais vue.
 *
 * Ce qui n'est pas une liste ne donne pas d'alerte, et ne lève pas non plus. Cette
 * fonction décide d'une fenêtre qui recouvre tout l'écran du dossier : une réponse d'une
 * forme inattendue doit y renoncer, jamais emporter l'écran avec elle.
 *
 * `imposable` à faux — le destinataire a désactivé l'urgence — n'impose plus rien. L'alerte
 * continue d'exister, de se lire et de se notifier ; elle cesse seulement de recouvrir
 * l'écran de quelqu'un qui a dit qu'elle ne le devait plus.
 */
export function alerteAImposer(
  alertes: unknown,
  monUserId: string | null,
  imposable = true,
): Alerte | null {
  if (!Array.isArray(alertes) || !imposable) return null;

  const candidates = (alertes as Alerte[])
    .filter(
      (alerte) =>
        alerte.level === 'urgent' &&
        alerte.acknowledgedAt === null &&
        alerte.authorId !== null &&
        alerte.authorId !== monUserId,
    )
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  return candidates[0] ?? null;
}
